import { useLayoutEffect, useRef, useState } from "react";

export interface TabsOption {
  value: string;
  label: string;
}

export interface TabsProps {
  tabs: TabsOption[];
  value: string;
  onChange: (value: string) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

export function Tabs({
  tabs,
  value,
  onChange,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: Readonly<TabsProps>) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const activeIndex = tabs.findIndex((tab) => tab.value === value);

  useLayoutEffect(() => {
    const measure = () => {
      const node = tabRefs.current[activeIndex];
      if (!node) {
        setIndicator(null);
        return;
      }

      setIndicator({ left: node.offsetLeft + 12, width: node.offsetWidth - 24 });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeIndex]);

  const commit = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab.value);
    tabRefs.current[index]?.focus();
  };

  const moveSelection = (direction: 1 | -1) => {
    if (tabs.length === 0) return;
    const currentIndex = tabs.findIndex((tab) => tab.value === value);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + direction + tabs.length) % tabs.length;
    commit(nextIndex);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        moveSelection(1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        moveSelection(-1);
        break;
      case "Home":
        event.preventDefault();
        if (tabs.length > 0) commit(0);
        break;
      case "End":
        event.preventDefault();
        if (tabs.length > 0) commit(tabs.length - 1);
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onKeyDown={handleKeyDown}
      className="relative flex items-center gap-1 border-b border-border-default"
    >
      {indicator && (
        <span
          aria-hidden="true"
          className="absolute -bottom-px h-0.5 rounded-full bg-brand-primary transition-all duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {tabs.map((tab, index) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => commit(index)}
            className={[
              "group relative shrink-0 cursor-pointer whitespace-nowrap px-3 py-2 text-sm font-semibold transition-colors",
              active ? "text-content-primary" : "text-content-tertiary hover:text-content-secondary",
            ].join(" ")}
          >
            {tab.label}
            {}
            {!active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-transparent transition-colors group-hover:bg-border-strong"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
